/**
 * Contains the Metadata static class, used to manipulate an editor's metadata properties
 */

import { type Editor } from 'slate';

interface AddMetadataOptions {
  key: string;
  value: any;
}

interface RemoveMetadataOptions {
  key: string;
}

interface UpdateMetadataOptions {
  key: string;
  value: any;
}

class Metadata {
  /**
   * Add a metadata property to the editor
   */
  static addMetadata(editor: Editor, options: AddMetadataOptions) {
    if (!editor) return;

    const { key, value } = options;
    if (!key) return;

    editor.apply({
      type: 'add_metadata',
      metadata: {
        key,
        value,
      },
    });
  }

  /**
   * Remove a metadata property from the editor
   */
  static removeMetadata(editor: Editor, options: RemoveMetadataOptions) {
    if (!editor) return;

    const { key } = options;
    if (!key) return;
    if (!editor.metadata || !(key in editor.metadata)) return;

    editor.apply({
      type: 'remove_metadata',
      metadata: {
        key,
        value: (editor.metadata as Record<string, any>)[key],
      },
    });
  }

  /**
   * Update a metadata property in the editor
   */
  static updateMetadata(editor: Editor, options: UpdateMetadataOptions) {
    if (!editor) return;

    const { key, value } = options;
    if (!key) return;
    if (!editor.metadata || !(key in editor.metadata)) return;

    editor.apply({
      type: 'set_metadata',
      properties: {
        key,
        value: (editor.metadata as Record<string, any>)[key],
      },
      newProperties: {
        key,
        value,
      },
    });
  }

  /**
   * Get a metadata property from the editor
   */
  static getMetadata(editor: Editor, key: string): any {
    if (!editor?.metadata) return undefined;
    return (editor.metadata as Record<string, any>)[key];
  }

  /**
   * Check if a metadata property exists in the editor
   */
  static hasMetadata(editor: Editor, key: string): boolean {
    if (!editor?.metadata) return false;
    return key in (editor.metadata as Record<string, any>);
  }

  /**
   * Get all metadata keys from the editor
   */
  static getMetadataKeys(editor: Editor): string[] {
    if (!editor?.metadata) return [];
    return Object.keys(editor.metadata).filter((key) => key !== 'annotations');
  }

  /**
   * Get all metadata as an object from the editor (excluding annotations)
   */
  static getAllMetadata(editor: Editor): Record<string, any> {
    if (!editor?.metadata) return {};

    const result: Record<string, any> = {};
    const keys = Metadata.getMetadataKeys(editor);

    for (const key of keys) {
      result[key] = (editor.metadata as Record<string, any>)[key];
    }

    return result;
  }
}

export default Metadata;
